<template>
  <b-row>
    <b-col>
      <b-overlay
        :show="loading"
        rounded
        opacity="0.6"
        spinner-variant="primary"
      >
        <b-card title="Shipment">
          <floating-title-badge
            v-if="!is_export"
            :message="ensLabelMessage"
            :is-success="ensCompliant"
          />
          <validation-observer
            ref="shipmentValidation"
          >
            <b-form ref="shipmentForm">
              <!-- Transport mode selector (airplane/truck) -->
              <b-row v-if="truckSettingExists">
                <b-col
                  sm="8"
                  md="6"
                  lg="6"
                >
                  <b-form-group
                    label="Transport mode"
                    label-for="transport-mode-radio-group"
                  >
                    <b-form-radio-group
                      id="transport-mode-radio-group"
                      v-model="defaultForm"
                      name="transport_mode"
                      :options="transportModeOptions"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  v-if="canSelectType"
                  sm="8"
                  md="6"
                  lg="6"
                >
                  <b-form-group
                    label="Type"
                    label-for="export-radio-group"
                  >
                    <b-form-radio-group
                      id="export-radio-group"
                      v-model="is_export"
                      name="is_export"
                      :options="exportOptions"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col cols="6">
                  <b-form-group
                    :label="defaultForm === 'road' ? 'Document number (CMR)' : 'Document number'"
                    label-for="document-number"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Document number"
                      rules="required"
                    >
                      <b-form-input
                        id="document-number"
                        v-model="document_number"
                        name="document-number"
                        :state="errors.length > 0 ? false:null"
                        autocomplete="off"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row v-if="defaultForm !== 'road'">
                <b-col cols="6">
                  <b-form-group
                    label="Origin"
                    label-for="origin"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Origin"
                      :rules="{ required: is_export }"
                    >
                      <v-select
                        id="origin"
                        ref="origin"
                        v-model="origin"
                        :options="airportsOptions"
                        name="origin"
                        placeholder="Type to search..."
                        :state="errors.length > 0 ? false:null"
                        @search="fetchAirports"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>

                <b-col cols="6">
                  <b-form-group
                    label="Destination"
                    label-for="destination"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Destination"
                      :rules="{ required: is_export }"
                    >
                      <v-select
                        id="destination"
                        ref="destination"
                        v-model="destination"
                        :options="airportsOptions"
                        name="destination"
                        placeholder="Type to search..."
                        :state="errors.length > 0 ? false:null"
                        @search="fetchAirports"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col
                  v-if="!is_export"
                  cols="6"
                  sm="8"
                  md="6"
                  lg="6"
                >
                  <b-form-group
                    label="Carrier"
                    label-for="carrier"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Carrier"
                      class="w-50"
                    >
                      <v-select
                        id="carrier"
                        ref="carrier"
                        v-model="carrier"
                        :placeholder="carriersOptions.length > 0 ? 'Select carrier' : ''"
                        :disabled="carriersOptions.length === 0"
                        :options="carriersOptions"
                        :loading="carriersListLoading"
                        name="carrier"
                        :state="errors.length > 0 ? false:null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                    <div
                      v-if="carriersOptions.length === 0 && carriersListLoading !== true"
                      class="text-muted text-sm"
                    >
                      <span v-if="!companiesList.length || (companiesList.length && companyId && !carriersOptions.length)">
                        Add carriers
                        <router-link :to="{name:'carriers'}">
                          here
                        </router-link>
                      </span>
                      <span v-else>Carriers list will be loaded once company is selected</span>
                    </div>
                  </b-form-group>
                </b-col>
                <b-col
                  v-if="!is_export"
                  cols="6"
                  sm="8"
                  md="6"
                  lg="6"
                >
                  <b-form-group
                    label="Entry country"
                    label-for="entry_country"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Entry country"
                      :rules="{ required: !is_export }"
                    >
                      <v-select
                        id="entry_country"
                        v-model="entry_country"
                        :options="filteredCustoms"
                        name="entry_country"
                        :state="errors.length > 0 ? false : null"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col
                  v-if="companyList.length > 1 && !is_export"
                  cols="6"
                  sm="8"
                  md="6"
                  lg="6"
                >
                  <b-form-group
                    label="Company"
                    label-for="company"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Company"
                      rules="required"
                    >
                      <v-select
                        id="company"
                        v-model="companyId"
                        :clearable="false"
                        :options="companiesList"
                        :disabled="!!$props.shipmentId"
                        @input="getCompanyConfigData"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
                <b-col
                  v-if="canAddGrossWeight"
                  cols="6"
                >
                  <b-form-group
                    label="Document gross weight"
                    label-for="document-gross-weight"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Document gross weight"
                      rules="required"
                    >
                      <b-form-input
                        id="document-gross-weight"
                        v-model="document_gross_weight"
                        name="document-gross-weight"
                        :disabled="is_pre_declaration_done"
                        :state="errors.length > 0 ? false:null"
                        autocomplete="off"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <!-- Truck specific blocks -->
              <template v-if="defaultForm === 'road'">
                <!-- Truck details block -->
                <b-row class="mt-1">
                  <b-col cols="12">
                    <h5>Truck details</h5>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Truck ID"
                      label-for="truck_id"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Truck ID"
                        rules="required"
                      >
                        <b-form-input
                          id="truck_id"
                          v-model="truck_id"
                          name="truck_id"
                          :state="errors.length > 0 ? false : null"
                          autocomplete="off"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Trailer ID"
                      label-for="trailer_id"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Trailer ID"
                      >
                        <b-form-input
                          id="trailer_id"
                          v-model="trailer_id"
                          name="trailer_id"
                          :state="errors.length > 0 ? false : null"
                          autocomplete="off"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Transport type"
                      label-for="transport_type"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Transport type"
                        rules="required"
                      >
                        <div class="d-flex align-items-start">
                          <v-select
                            id="transport_type"
                            v-model="transport_type"
                            :options="transportTypeOptions"
                            name="transport_type"
                            placeholder="Select transport type"
                            :state="errors.length > 0 ? false : null"
                            class="flex-grow-1"
                          />
                          <b-button
                            variant="outline-info"
                            size="sm"
                            class="ml-1"
                            @click="showTransportTypeModal = true"
                          >
                            <span>ℹ️</span>
                          </b-button>
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Nationality"
                      label-for="nationality"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Nationality"
                        rules="required"
                      >
                        <v-select
                          id="nationality"
                          v-model="nationality"
                          :options="allCountriesOptions"
                          name="nationality"
                          placeholder="Select nationality"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Departure/Arrival block -->
                <b-row class="mt-1">
                  <b-col cols="12">
                    <h5>Departure/Arrival</h5>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="4">
                    <b-form-group
                      label="Actual departure time"
                      label-for="actual_departure_time"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Actual departure time"
                        :rules="{ required: !estimated_departure_time }"
                      >
                        <b-form-input
                          id="actual_departure_time"
                          v-model="actual_departure_time"
                          type="datetime-local"
                          name="actual_departure_time"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] || ((!actual_departure_time && !estimated_departure_time) ? 'At least one departure time is required' : '') }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="4">
                    <b-form-group
                      label="Estimated departure time"
                      label-for="estimated_departure_time"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Estimated departure time"
                        :rules="{ required: !actual_departure_time }"
                      >
                        <b-form-input
                          id="estimated_departure_time"
                          v-model="estimated_departure_time"
                          type="datetime-local"
                          name="estimated_departure_time"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] || ((!actual_departure_time && !estimated_departure_time) ? 'At least one departure time is required' : '') }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="4">
                    <b-form-group
                      label="Estimated arrival time"
                      label-for="estimated_arrival_time"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Estimated arrival time"
                        rules="required"
                      >
                        <b-form-input
                          id="estimated_arrival_time"
                          v-model="estimated_arrival_time"
                          type="datetime-local"
                          name="estimated_arrival_time"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Locations block -->
                <b-row class="mt-1">
                  <b-col cols="12">
                    <h5>Locations</h5>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of acceptance location"
                      label-for="place_of_acceptance_location"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of acceptance location"
                        rules="required|max:35"
                      >
                        <b-form-input
                          id="place_of_acceptance_location"
                          v-model="place_of_acceptance_location"
                          name="place_of_acceptance_location"
                          :state="errors.length > 0 ? false : null"
                          autocomplete="off"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of acceptance country"
                      label-for="place_of_acceptance_country"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of acceptance country"
                        rules="required"
                      >
                        <v-select
                          id="place_of_acceptance_country"
                          v-model="place_of_acceptance_country"
                          :options="allCountriesOptions"
                          name="place_of_acceptance_country"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of loading location"
                      label-for="place_of_loading_location"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of loading location"
                        rules="required|max:35"
                      >
                        <b-form-input
                          id="place_of_loading_location"
                          v-model="place_of_loading_location"
                          name="place_of_loading_location"
                          :state="errors.length > 0 ? false : null"
                          autocomplete="off"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of loading country"
                      label-for="place_of_loading_country"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of loading country"
                        rules="required"
                      >
                        <v-select
                          id="place_of_loading_country"
                          v-model="place_of_loading_country"
                          :options="allCountriesOptions"
                          name="place_of_loading_country"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of unloading location"
                      label-for="place_of_unloading_location"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of unloading location"
                        rules="required|max:35"
                      >
                        <b-form-input
                          id="place_of_unloading_location"
                          v-model="place_of_unloading_location"
                          name="place_of_unloading_location"
                          :state="errors.length > 0 ? false : null"
                          autocomplete="off"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of unloading country"
                      label-for="place_of_unloading_country"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of unloading country"
                        rules="required"
                      >
                        <v-select
                          id="place_of_unloading_country"
                          v-model="place_of_unloading_country"
                          :options="allCountriesOptions"
                          name="place_of_unloading_country"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of delivery location"
                      label-for="place_of_delivery_location"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of delivery location"
                        rules="required|max:35"
                      >
                        <b-form-input
                          id="place_of_delivery_location"
                          v-model="place_of_delivery_location"
                          name="place_of_delivery_location"
                          :state="errors.length > 0 ? false : null"
                          autocomplete="off"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Place of delivery country"
                      label-for="place_of_delivery_country"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Place of delivery country"
                        rules="required"
                      >
                        <v-select
                          id="place_of_delivery_country"
                          v-model="place_of_delivery_country"
                          :options="allCountriesOptions"
                          name="place_of_delivery_country"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Routes block -->
                <b-row class="mt-1">
                  <b-col cols="12">
                    <h5>Routes</h5>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="First routing country"
                      label-for="first_routing_country"
                    >
                      <v-select
                        id="first_routing_country"
                        :value="first_routing_country"
                        :options="allCountriesOptions"
                        name="first_routing_country"
                        disabled
                        placeholder="Auto-populated from loading country"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Intermediate routing countries -->
                <b-row
                  v-for="(country, index) in intermediate_routing_countries"
                  :key="`intermediate-country-${index}`"
                >
                  <b-col cols="6">
                    <b-form-group
                      :label="`Intermediate routing country`"
                      :label-for="`intermediate_routing_country_${index}`"
                    >
                      <validation-provider
                        #default="{ errors }"
                        :name="`Intermediate routing country`"
                        rules="required"
                      >
                        <v-select
                          :id="`intermediate_routing_country_${index}`"
                          v-model="intermediate_routing_countries[index]"
                          :options="availableRoutingCountries"
                          :name="`intermediate_routing_country_${index}`"
                          :state="errors.length > 0 ? false : null"
                          placeholder="Select country"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col
                    cols="1"
                    class="d-flex align-items-center"
                  >
                    <b-button
                      variant="outline-danger"
                      size="sm"
                      @click="removeIntermediateCountry(index)"
                    >
                      <span>&times;</span>
                    </b-button>
                  </b-col>
                </b-row>

                <b-row>
                  <b-col cols="6">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      @click="addIntermediateCountry"
                    >
                      Add country
                    </b-button>
                  </b-col>
                </b-row>

                <b-row class="mt-1">
                  <b-col cols="6">
                    <b-form-group
                      label="Last routing country"
                      label-for="last_routing_country"
                    >
                      <v-select
                        id="last_routing_country"
                        :value="last_routing_country"
                        :options="allCountriesOptions"
                        name="last_routing_country"
                        disabled
                        placeholder="Auto-populated from delivery country"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Consignee data block -->
                <b-row class="mt-3">
                  <b-col cols="12">
                    <h5>Consignee data</h5>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignee name"
                      label-for="consignee_name"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignee name"
                        rules="required"
                      >
                        <b-form-input
                          id="consignee_name"
                          v-model="consignee_name"
                          name="consignee_name"
                          placeholder="Enter consignee name"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignee type of person"
                      label-for="consignee_type_of_person"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignee type of person"
                        rules="required"
                      >
                        <v-select
                          id="consignee_type_of_person"
                          v-model="consignee_type_of_person"
                          :options="consigneeTypeOfPersonOptions"
                          name="consignee_type_of_person"
                          placeholder="Select consignee type of person"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignee city"
                      label-for="consignee_city"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignee city"
                        rules="required"
                      >
                        <b-form-input
                          id="consignee_city"
                          v-model="consignee_city"
                          name="consignee_city"
                          placeholder="Enter consignee city"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignee country"
                      label-for="consignee_country"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignee country"
                        rules="required"
                      >
                        <v-select
                          id="consignee_country"
                          v-model="consignee_country"
                          :options="allCountriesOptions"
                          name="consignee_country"
                          placeholder="Select consignee country"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignee street"
                      label-for="consignee_street"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignee street"
                        rules="required"
                      >
                        <b-form-input
                          id="consignee_street"
                          v-model="consignee_street"
                          name="consignee_street"
                          placeholder="Enter consignee street"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignee street number"
                      label-for="consignee_street_number"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignee street number"
                        rules="required"
                      >
                        <b-form-input
                          id="consignee_street_number"
                          v-model="consignee_street_number"
                          name="consignee_street_number"
                          placeholder="Enter consignee street number"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignee postcode"
                      label-for="consignee_postcode"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignee postcode"
                        rules="required"
                      >
                        <b-form-input
                          id="consignee_postcode"
                          v-model="consignee_postcode"
                          name="consignee_postcode"
                          placeholder="Enter consignee postcode"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Consignor data block -->
                <b-row class="mt-3">
                  <b-col cols="12">
                    <h5>Consignor data</h5>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor name"
                      label-for="consignor_name"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor name"
                        rules="required"
                      >
                        <b-form-input
                          id="consignor_name"
                          v-model="consignor_name"
                          name="consignor_name"
                          placeholder="Enter consignor name"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor type of person"
                      label-for="consignor_type_of_person"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor type of person"
                        rules="required"
                      >
                        <v-select
                          id="consignor_type_of_person"
                          v-model="consignor_type_of_person"
                          :options="consignorTypeOfPersonOptions"
                          name="consignor_type_of_person"
                          placeholder="Select consignor type of person"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor city"
                      label-for="consignor_city"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor city"
                        rules="required"
                      >
                        <b-form-input
                          id="consignor_city"
                          v-model="consignor_city"
                          name="consignor_city"
                          placeholder="Enter consignor city"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor country"
                      label-for="consignor_country"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor country"
                        rules="required"
                      >
                        <v-select
                          id="consignor_country"
                          v-model="consignor_country"
                          :options="allCountriesOptions"
                          name="consignor_country"
                          placeholder="Select consignor country"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor street"
                      label-for="consignor_street"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor street"
                        rules="required"
                      >
                        <b-form-input
                          id="consignor_street"
                          v-model="consignor_street"
                          name="consignor_street"
                          placeholder="Enter consignor street"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor street number"
                      label-for="consignor_street_number"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor street number"
                        rules="required"
                      >
                        <b-form-input
                          id="consignor_street_number"
                          v-model="consignor_street_number"
                          name="consignor_street_number"
                          placeholder="Enter consignor street number"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor postcode"
                      label-for="consignor_postcode"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor postcode"
                        rules="required"
                      >
                        <b-form-input
                          id="consignor_postcode"
                          v-model="consignor_postcode"
                          name="consignor_postcode"
                          placeholder="Enter consignor postcode"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      label="Consignor region"
                      label-for="consignor_region"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Consignor region"
                        rules="required"
                      >
                        <b-form-input
                          id="consignor_region"
                          v-model="consignor_region"
                          name="consignor_region"
                          placeholder="Enter consignor region"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Customs block -->
                <b-row class="mt-3">
                  <b-col cols="12">
                    <h5>Customs</h5>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="6">
                    <b-form-group
                      label="Customs office of first entry"
                      label-for="customs_office_of_first_entry"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Customs office of first entry"
                        rules="required|length:8"
                      >
                        <b-form-input
                          id="customs_office_of_first_entry"
                          v-model="customs_office_of_first_entry"
                          name="customs_office_of_first_entry"
                          placeholder="Enter customs office code (8 characters)"
                          maxlength="8"
                          :state="errors.length > 0 ? false : null"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </template>

              <b-row v-if="canSelectDeclarationType">
                <b-col
                  sm="8"
                  md="6"
                  lg="6"
                >
                  <b-form-group
                    label="Declaration type"
                    label-for="declaration-type-radio-group"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Declaration type"
                      rules="required"
                    >
                      <b-form-radio-group
                        id="declaration-type-radio-group"
                        v-model="declaration_type"
                        name="declaration_type"
                        :options="declarationTypes"
                        :state="errors.length > 0 ? false:null"
                        :disabled="shipmentId"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </b-col>
              </b-row>

              <b-row class="mt-1">
                <b-col>
                  <b-overlay
                    :show="formSubmission"
                    rounded
                    opacity="0.6"
                    spinner-small
                    spinner-variant="primary"
                    class="d-inline-block"
                  >
                    <b-button
                      id="submit-shipment"
                      type="button"
                      variant="primary"
                      @click="validationForm"
                    >
                      Submit
                    </b-button>
                  </b-overlay>
                </b-col>
              </b-row>
            </b-form>
          </validation-observer>
        </b-card>
      </b-overlay>
    </b-col>

    <!-- Transport Type Options Modal -->
    <b-modal
      v-model="showTransportTypeModal"
      title="Transport Type Options"
      size="lg"
      ok-only
      ok-title="Close"
    >
      <p class="mb-2">
        <strong>All available transport types:</strong>
      </p>
      <div
        style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; font-family: monospace; white-space: pre-wrap; max-height: 400px; overflow-y: auto;"
      >
        <div
          v-for="(option, index) in transportTypeOptions"
          :key="index"
          style="margin-bottom: 8px;"
        >
          {{ option.label }}
        </div>
      </div>
      <p class="mt-3 text-muted small">
        <em>Tip: You can select and copy this entire list for your reference.</em>
      </p>
    </b-modal>
  </b-row>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BOverlay,
  BFormRadioGroup,
  BModal,
} from 'bootstrap-vue'

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate/dist/vee-validate.full'
import shipmentModel from '@/views/shipments/shipmentModel'
import vSelect from 'vue-select'
import FloatingTitleBadge from '@/layouts/components/FloatingTitleBadge.vue'
import handleError from '@/views/components/errorHandler'
import { allCountries } from '@/data/countries'
import transportTypes from '@/data/transportTypes'
import ensCompliantUtil from '@core/utils/ensCompliant'

export default {
  components: {
    BFormRadioGroup,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BCard,
    BOverlay,
    BModal,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    FloatingTitleBadge,
  },
  props: {
    shipmentId: {
      type: [String, Number],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      timer: null,
      documentTypeList: [],
      loading: false,
      formSubmission: false,
      carriersListLoading: false,
      is_pre_declaration_done: false,
      document_number: '',
      document_gross_weight: '',
      defaultForm: 'airplane',
      truckSettingExists: false,
      showTransportTypeModal: false,
      // Truck-specific fields
      truck_id: '',
      trailer_id: '',
      transport_type: null,
      nationality: null,
      actual_departure_time: null,
      estimated_departure_time: null,
      estimated_arrival_time: null,
      // Locations fields
      place_of_acceptance_location: '',
      place_of_acceptance_country: null,
      place_of_delivery_location: '',
      place_of_delivery_country: null,
      place_of_loading_location: '',
      place_of_loading_country: null,
      place_of_unloading_location: '',
      place_of_unloading_country: null,
      // Routes fields
      intermediate_routing_countries: [],
      // Consignee data fields
      consignee_name: '',
      consignee_type_of_person: null,
      consignee_city: '',
      consignee_country: null,
      consignee_street: '',
      consignee_street_number: '',
      consignee_postcode: '',
      // Consignor data fields
      consignor_name: '',
      consignor_type_of_person: null,
      consignor_city: '',
      consignor_country: null,
      consignor_street: '',
      consignor_street_number: '',
      consignor_postcode: '',
      consignor_region: '',
      // Customs fields
      customs_office_of_first_entry: '',
      origin: null,
      destination: null,
      carrier: null,
      is_export: false,
      declaration_type: null,
      entry_country: '',
      airportsOptions: [],
      carriersOptions: [],
      customsOptions: [],
      allCountriesOptions: allCountries,
      documentTypes: [],
      companyList: [],
      companyId: null,
      airportsTyping: false,
      carriersTyping: false,
      transportModeOptions: [
        { text: 'Air', value: 'airplane' },
        { text: 'Road', value: 'road' },
      ],
      // Select options for truck fields
      transportTypeOptions: transportTypes,
      nationalityOptions: [],
      consigneeTypeOfPersonOptions: [
        { label: 'Natural person', value: '1' },
        { label: 'Legal person', value: '2' },
        { label: 'Association of persons', value: '3' },
      ],
      consignorTypeOfPersonOptions: [
        { label: 'Natural person', value: '1' },
        { label: 'Legal person', value: '2' },
        { label: 'Association of persons', value: '3' },
      ],
      exportOptions: [
        { text: 'Import', value: false },
        { text: 'Export', value: true },
      ],
      declarationTypes: [
        { text: 'H7', value: 'H7' },
        { text: 'H1', value: 'H1' },
      ],
    }
  },
  computed: {
    ensCompliant() {
      return ensCompliantUtil(this)
    },
    ensLabelMessage() {
      return this.ensCompliant ? 'ENS compliant' : 'not compliant to ENS format'
    },
    companiesList() {
      const filtered = []
      this.companyList.forEach(item => {
        filtered.push({
          label: item.name,
          value: item.id,
        })
      })

      return filtered
    },
    filteredCustoms() {
      if (this.declaration_type === 'H1') {
        return this.customsOptions.filter(x => this.$classifiers().classifierContains('h1_countries', x.value))
      }
      return this.customsOptions
    },
    company_id() {
      return this.companyId?.value || this.$activeCompany().data.company.id
    },
    selectedCompany() {
      if (
        (this.companyList.length > 1 && this.companyId)
        || (this.companyList.length <= 1 && this.company_id)
      ) {
        return this.companyList.find(x => x.id === this.company_id)
      }
      return null
    },
    canAddGrossWeight() {
      return this.selectedCompany && this.selectedCompany.config?.adjustItemsWeight
    },
    canSelectDeclarationType() {
      return !this.is_export && this.selectedCompany && this.selectedCompany.config?.h1
    },
    canSelectType() {
      return this.selectedCompany && this.selectedCompany.tags?.includes('Can make export shipments')
    },
    // Routes computed properties
    first_routing_country() {
      return this.place_of_loading_country
    },
    last_routing_country() {
      return this.place_of_delivery_country
    },
    flatRoutingArray() {
      // Generate flat array: [first, ...intermediates, last]
      const route = []
      if (this.first_routing_country) {
        route.push(this.first_routing_country.value)
      }
      this.intermediate_routing_countries.forEach(country => {
        if (country) {
          route.push(country.value)
        }
      })
      if (this.last_routing_country) {
        route.push(this.last_routing_country.value)
      }
      return route
    },
    availableRoutingCountries() {
      // Filter out countries already in the route to enforce uniqueness
      const usedCountries = this.flatRoutingArray
      return this.allCountriesOptions.filter(country => !usedCountries.includes(country.value))
    },
  },
  watch: {
    filteredCustoms() {
      // entry country can only be from h1 countries list
      if (this.declaration_type === 'H1' && this.entry_country) {
        this.entry_country = this.filteredCustoms.find(x => x.value === this.selectedCompany.country)
      }
    },
    defaultForm(newVal) {
      // When switching to truck, clear airport selections to avoid stale values
      if (newVal === 'road') {
        this.origin = null
        this.destination = null
      }
      // Reload carriers list to apply filtering based on transport mode
      if (this.company_id) {
        this.getCarriersList()
      }
    },
  },
  async created() {
    this.$data.companyList = this.$profile().getUserAndAuthorizedCompanies()
    if (
      (this.$data.companyList.length <= 1 && !this.is_export)
      || (this.$props.shipmentId && this.company_id)
    ) {
      await this.getCompanyConfigData()
    }
    await this.$http.get('/v1/customs/available')
      .then(response => {
        const availableCustoms = response.data.data
        this.customsOptions = availableCustoms.map(customs => ({ value: customs.country_code, label: customs.name }))
      })
    if (this.$props.shipmentId) {
      this.loading = true
      shipmentModel().getShipment(this.$props.shipmentId, this)
        .then(response => {
          this.companyId = this.companiesList.find(x => x.value === response.data.company_id)
          this.document_number = response.data.document_number
          this.document_gross_weight = response.data.document_gross_weight
          this.is_pre_declaration_done = response.data.is_pre_declaration_done
          this.declaration_type = response.data.declaration_type
          if (response.data.origin_airport) {
            this.origin = this.getAiportOption(response.data.origin_airport)
            this.airportsOptions.push(this.origin)
          }
          if (response.data.destination_airport) {
            this.destination = this.getAiportOption(response.data.destination_airport)
            if (this.airportsOptions.find(x => x.value === this.destination.value) === undefined) {
              this.airportsOptions.push(this.destination)
            }
          }
          if (response.data.carrier) {
            if (this.carriersOptions.find(x => x.value === response.data.carrier.id) === undefined) {
              this.carriersOptions.push(this.getCarrierOption(response.data.carrier))
            }
            this.carrier = this.getCarrierOption(response.data.carrier)
          }
          if (response.data.entry_country && this.customsOptions) {
            this.entry_country = this.customsOptions.find(x => x.value === response.data.entry_country)
          }
          // Load default_form (transport mode)
          if (response.data.default_form) {
            this.defaultForm = response.data.default_form
          }
          // Load conveyance data (truck-specific fields)
          if (response.data.conveyance) {
            const { conveyance } = response.data

            // Truck details
            if (conveyance.truck_id) this.truck_id = conveyance.truck_id
            if (conveyance.trailer_id) this.trailer_id = conveyance.trailer_id
            if (conveyance.transport_type) {
              this.transport_type = this.transportTypeOptions.find(x => x.value === conveyance.transport_type)
            }
            if (conveyance.nationality) {
              this.nationality = this.allCountriesOptions.find(x => x.value === conveyance.nationality)
            }

            // Departure/Arrival times
            if (conveyance.actual_departure_time) {
              // Convert ISO datetime to datetime-local format (YYYY-MM-DDTHH:mm)
              this.actual_departure_time = conveyance.actual_departure_time.substring(0, 16)
            }
            if (conveyance.estimated_departure_time) {
              this.estimated_departure_time = conveyance.estimated_departure_time.substring(0, 16)
            }
            if (conveyance.estimated_arrival_time) {
              this.estimated_arrival_time = conveyance.estimated_arrival_time.substring(0, 16)
            }

            // Locations
            if (conveyance.place_of_acceptance_location) {
              this.place_of_acceptance_location = conveyance.place_of_acceptance_location
            }
            if (conveyance.place_of_acceptance_country) {
              this.place_of_acceptance_country = this.allCountriesOptions.find(x => x.value === conveyance.place_of_acceptance_country)
            }
            if (conveyance.place_of_delivery_location) {
              this.place_of_delivery_location = conveyance.place_of_delivery_location
            }
            if (conveyance.place_of_delivery_country) {
              this.place_of_delivery_country = this.allCountriesOptions.find(x => x.value === conveyance.place_of_delivery_country)
            }
            if (conveyance.place_of_loading_location) {
              this.place_of_loading_location = conveyance.place_of_loading_location
            }
            if (conveyance.place_of_loading_country) {
              this.place_of_loading_country = this.allCountriesOptions.find(x => x.value === conveyance.place_of_loading_country)
            }
            if (conveyance.place_of_unloading_location) {
              this.place_of_unloading_location = conveyance.place_of_unloading_location
            }
            if (conveyance.place_of_unloading_country) {
              this.place_of_unloading_country = this.allCountriesOptions.find(x => x.value === conveyance.place_of_unloading_country)
            }

            // Routing countries (intermediate)
            if (conveyance.routing_countries && Array.isArray(conveyance.routing_countries)) {
              // Extract intermediate countries (excluding first and last)
              const intermediates = conveyance.routing_countries.slice(1, -1)
              this.intermediate_routing_countries = intermediates.map(countryCode => this.allCountriesOptions.find(x => x.value === countryCode)).filter(x => x !== undefined)
            }

            // Consignee data
            if (conveyance.consignee_name) this.consignee_name = conveyance.consignee_name
            if (conveyance.consignee_type_of_person) {
              this.consignee_type_of_person = this.consigneeTypeOfPersonOptions.find(x => x.value === conveyance.consignee_type_of_person)
            }
            if (conveyance.consignee_city) this.consignee_city = conveyance.consignee_city
            if (conveyance.consignee_country) {
              this.consignee_country = this.allCountriesOptions.find(x => x.value === conveyance.consignee_country)
            }
            if (conveyance.consignee_street) this.consignee_street = conveyance.consignee_street
            if (conveyance.consignee_street_number) this.consignee_street_number = conveyance.consignee_street_number
            if (conveyance.consignee_postcode) this.consignee_postcode = conveyance.consignee_postcode

            // Consignor data
            if (conveyance.consignor_name) this.consignor_name = conveyance.consignor_name
            if (conveyance.consignor_type_of_person) {
              this.consignor_type_of_person = this.consignorTypeOfPersonOptions.find(x => x.value === conveyance.consignor_type_of_person)
            }
            if (conveyance.consignor_city) this.consignor_city = conveyance.consignor_city
            if (conveyance.consignor_country) {
              this.consignor_country = this.allCountriesOptions.find(x => x.value === conveyance.consignor_country)
            }
            if (conveyance.consignor_street) this.consignor_street = conveyance.consignor_street
            if (conveyance.consignor_street_number) this.consignor_street_number = conveyance.consignor_street_number
            if (conveyance.consignor_postcode) this.consignor_postcode = conveyance.consignor_postcode
            if (conveyance.consignor_region) this.consignor_region = conveyance.consignor_region

            // Customs office
            if (conveyance.customs_office_of_first_entry) {
              this.customs_office_of_first_entry = conveyance.customs_office_of_first_entry
            }
          }
          this.loading = false
          if (this.canSelectType) {
            this.is_export = response.data.is_export
          }
        })
    }
  },
  methods: {
    getCompanyConfigData() {
      this.getCarriersList()
      this.checkEnsSettings()
    },
    getCarriersList() {
      if (!this.shipmentId && this.companiesList.length > 1 && !this.companyId) return

      this.carriersListLoading = true
      this.$http.get(`/v1/companies/${this.company_id}/carriers`)
        .then(response => {
          let carriersList = response.data.data

          // Filter carriers based on transport mode
          if (this.defaultForm === 'road') {
            // For road form, show only carriers with type 'plane'
            carriersList = carriersList.filter(carrier => carrier.type === 'truck')
          }
          // For air form, show all carriers (no filtering)

          this.carriersOptions = carriersList.map(carrier => (this.getCarrierOption(carrier)))
          this.carriersListLoading = false
          if (this.carriersOptions.length === 1) {
            [this.carrier] = this.carriersOptions
          }
        })
    },
    checkEnsSettings() {
      this.getEnsSettings()
    },
    getEnsSettings() {
      if (!this.shipmentId && this.companiesList.length > 1 && !this.companyId) return

      this.loading = true
      this.$http.get(`/v1/companies/${this.company_id}/ensSettings`)
        .then(response => {
          this.truckSettingExists = response.data.data.find(setting => setting.message_type === 'F50')
        })
        .catch(error => {
          handleError(error, this.$toast)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getAiportOption(airport) {
      if (airport) {
        return {
          value: airport.id,
          label: `${airport.code} ${airport.name}, ${airport.country_code}`,
        }
      }
      return null
    },
    fetchAirports(search, loading) {
      if (search.length >= 2 && !this.airportsTyping) {
        loading(true)
        clearTimeout(this.timer)
        this.timer = setTimeout(() => {
          this.airportsTyping = true
          this.$http.get(`/v1/airports?keyword=${encodeURIComponent(search)}`)
            .then(response => {
              loading(false)
              this.airportsTyping = false
              const airportsList = response.data.data
              this.airportsOptions = airportsList.map(airport => (this.getAiportOption(airport)))
            })
        }, 500)
      }
    },
    getCarrierOption(carrier) {
      if (carrier) {
        return {
          value: carrier.id,
          label: `${carrier.code} ${carrier.name}`,
        }
      }
      return null
    },
    // Routes methods
    addIntermediateCountry() {
      this.intermediate_routing_countries.push(null)
    },
    removeIntermediateCountry(index) {
      this.intermediate_routing_countries.splice(index, 1)
    },
    validationForm() {
      this.$refs.shipmentValidation.validate().then(success => {
        if (!success) {
          return
        }
        this.editShipment()
      })
    },
    editShipment() {
      let queryHandle = {}
      this.formSubmission = true
      let declarationType = null
      if (!this.is_export) {
        declarationType = this.$data.declaration_type || 'H7'
      }

      const data = {
        document_number: this.$data.document_number,
        document_gross_weight: this.canAddGrossWeight && !this.is_pre_declaration_done ? this.$data.document_gross_weight : null,
        origin: this.$data.origin ? this.$data.origin.value : null,
        destination: this.$data.destination ? this.$data.destination.value : null,
        carrier: this.$data.carrier ? this.$data.carrier.value : null,
        entry_country: this.$data.entry_country ? this.$data.entry_country.value : null,
        is_export: this.$data.is_export,
        declaration_type: declarationType,
        default_form: this.defaultForm,
      }

      // Add truck-specific fields for truck mode
      if (this.defaultForm === 'road') {
        // Add routing countries
        if (this.flatRoutingArray.length > 0) {
          data.routing_countries = this.flatRoutingArray
        }

        // Add truck details
        data.truck_id = this.$data.truck_id
        data.trailer_id = this.$data.trailer_id
        data.transport_type = this.$data.transport_type ? this.$data.transport_type.value : null
        data.nationality = this.$data.nationality ? this.$data.nationality.value : null

        // Add departure/arrival times
        data.actual_departure_time = this.$data.actual_departure_time
        data.estimated_departure_time = this.$data.estimated_departure_time
        data.estimated_arrival_time = this.$data.estimated_arrival_time

        // Add location fields
        data.place_of_acceptance_location = this.$data.place_of_acceptance_location
        data.place_of_acceptance_country = this.$data.place_of_acceptance_country ? this.$data.place_of_acceptance_country.value : null
        data.place_of_delivery_location = this.$data.place_of_delivery_location
        data.place_of_delivery_country = this.$data.place_of_delivery_country ? this.$data.place_of_delivery_country.value : null
        data.place_of_loading_location = this.$data.place_of_loading_location
        data.place_of_loading_country = this.$data.place_of_loading_country ? this.$data.place_of_loading_country.value : null
        data.place_of_unloading_location = this.$data.place_of_unloading_location
        data.place_of_unloading_country = this.$data.place_of_unloading_country ? this.$data.place_of_unloading_country.value : null

        // Add consignee data fields
        data.consignee_name = this.$data.consignee_name
        data.consignee_type_of_person = this.$data.consignee_type_of_person ? this.$data.consignee_type_of_person.value : null
        data.consignee_city = this.$data.consignee_city
        data.consignee_country = this.$data.consignee_country ? this.$data.consignee_country.value : null
        data.consignee_street = this.$data.consignee_street
        data.consignee_street_number = this.$data.consignee_street_number
        data.consignee_postcode = this.$data.consignee_postcode

        // Add consignor data fields
        data.consignor_name = this.$data.consignor_name
        data.consignor_type_of_person = this.$data.consignor_type_of_person ? this.$data.consignor_type_of_person.value : null
        data.consignor_city = this.$data.consignor_city
        data.consignor_country = this.$data.consignor_country ? this.$data.consignor_country.value : null
        data.consignor_street = this.$data.consignor_street
        data.consignor_street_number = this.$data.consignor_street_number
        data.consignor_postcode = this.$data.consignor_postcode
        data.consignor_region = this.$data.consignor_region

        // Add customs office field
        data.customs_office_of_first_entry = this.$data.customs_office_of_first_entry
      }
      if (!this.$props.shipmentId) {
        queryHandle = this.$http.post(`/v1/companies/${this.company_id}/shipments`, data)
      } else {
        queryHandle = this.$http.put(`/v1/companies/${this.company_id}/shipments/${this.$props.shipmentId}`, data)
      }
      queryHandle.then(response => {
        if (!this.$props.shipmentId) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Shipment added',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
          this.$router.push({ name: 'shipment', params: { shipmentId: response.data.shipment_id } })
        } else {
          this.formSubmission = false
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Shipment edited',
              icon: 'EditIcon',
              variant: 'success',
            },
          })
          this.$router.push({ name: 'shipment' })
        }
      })
        .catch(error => {
          this.formSubmission = false
          handleError(error, this.$toast)
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
